import { ChangeDetectorRef, Injectable, OnInit } from "@angular/core";
import { DestroyBase } from "../components/common/ng-destroy-base";
import { ProjectValuationTabs } from "./enum";
import { NavigationService } from "./navigation.service";
import { EventBusService } from "../services/event-bus.service";
import { AppStorageService } from "../services/app-storage.service";

/**
 * Navigation action base class
 */
@Injectable()
export class NavigationActionBase extends DestroyBase implements OnInit {
  /**
   * Indicates if the info bar (right) is visible
   */
  infoBarVisible = false;

  /**
   * Indicates if the user is an Admin
   */
  isAdminUser = false;

  /**
   * Indicates if the user has role Project Team
   */
  isProjectTeam = false;

  /**
   * Indicates if the user has role Valuer
   */
  isValuer = false;

  /**
   * Indicates if the user has role Client Account
   */
  isClientAccount = false;

  /**
   * Indicates if the user has role Geo Lead
   */
  isGeoLead = false;

  /**
   * Indicates if the user has role Sector Lead
   */
  isSectorLead = false;

  /**
   * Indicates if the user has role Business Analyst
   */
  isBusinessAnalyst = false;

  /**
   * Indicates if the user has role Dataverse Developer
   */
  isDataverseDeveloper = false;

  /**
   * Indicates if the user has role Dataverse Consultant
   */
  isDataverseConsultant = false;

  /**
   * Indicates if the user has role Dataverse Key User
   */
  isDataverseKeyUser = false;

  /**
 * Indicates if the user is an Admin OR Project Team
 */
  isAPT = false;

  /**
* Indicates if the user is an Admin OR Project Team OR Valuer
*/
  isAPTV = false;

  /**
   * Indicates if the Comparables Export button is visible
   */
  comparablesExportBtnVisible = false;

  /**
   * Toggle Organization display.
   */
  toggleOrganization = false;

  /**
   * Toggle data display.
   */
  toggleData = false;

  /**
   * The selected grouping item
   */
  groupingItemSelected = null;

  /**
   * Display save / cancel options
   */
  displaySaveCancelActions = false;

  /**
   * Is an edit form opened?
   */
  formOpened = false;

  /**
 * Is save in progress?
 */
  saveInProgress = false;
  /**
   * Constructor
   * @param appStorage
   * @param eventBus
   * @param ref
   * @param navigationService
   */
  constructor(protected appStorage: AppStorageService,
    protected eventBus: EventBusService,
    public ref: ChangeDetectorRef,
    private navigationService: NavigationService) {
    super();
  }
  /**
   * Initializes the service
   */
  ngOnInit(): void {
    this.handleEvents();
    this.handleFormOpenStateEvents();
    setTimeout(() => {
      this.setRolesForUser();
    });
  }
  /**
   * Trigger action
   * @param action
   * @param keepInfobarOpen
   */
  triggerAction(action: string, keepInfobarOpen = false) {
    if (!keepInfobarOpen) {
      this.eventBus.broadcastEvent('buttonTogglePVInfoMode', false);//close infobar if some action is triggered
    }

    this.eventBus.broadcastEvent(action);
  }
  /**
   * Set info bar visibility
   */
  setinfoBarVisibility() {
    this.infoBarVisible = !this.infoBarVisible;
    this.eventBus.broadcastEvent('buttonTogglePVInfoMode', this.infoBarVisible);
  }
  /**
   * Set info bar visibility comparables
   */
  setinfoBarVisibilityComparables() {
    this.eventBus.broadcastEvent('buttonTogglePVInfoModeComp');
  }
  /**
   * Open new tab and trigger action
   * @param action
   * @param event
   * @param tab
   */
  openNewTabAndTriggerAction(action: string, event: string, tab: string) {
    this.eventBus.broadcastEvent(event, tab, true);
    this.setActiveTab(tab);
    this.triggerAction(action);
  }
  /**
   * Action grouping logic
   * @param groupingItem
   */
  onGroupingItemSelected(groupingItem: string) {
    switch (groupingItem) {
      case 'Organization':
        this.toggleOrganization = this.switchChevronIcon(this.toggleOrganization, groupingItem);
        if (this.toggleOrganization) {
          this.eventBus.broadcastEvent('triggerShortNavigation');
        }
        else {
          this.eventBus.broadcastEvent('resetShortNavigation');
        }
        break;
      case 'Data':
        this.toggleData = this.switchChevronIcon(this.toggleData, groupingItem);
        if (this.toggleData) {
          this.eventBus.broadcastEvent('triggerShortNavigation');
        }
        else {
          this.eventBus.broadcastEvent('resetShortNavigation');
        }

        this.eventBus.broadcastEvent('buttonTogglePVInfoMode', false);

        break;
      default:
        this.groupingItemSelected = null;
    }

    this.ref.markForCheck();
  }
  /**
   * Switch chevron icon
   * @param flag
   * @param groupingItem
   * @returns
   */
  switchChevronIcon(flag: boolean, groupingItem: string) {
    this.resetAllToggle();
    if (flag) {
      this.groupingItemSelected = null;
      return false;
    } else {
      this.groupingItemSelected = null;
      this.groupingItemSelected = groupingItem;
      return true;
    }
  }
  /**
   * Reset all toggle
   */
  resetAllToggle() {
    this.toggleOrganization = false;
    this.toggleData = false;
  }

  /** Save/Cancel logic **/
  saveCancelActions(action: string) {
    if (!this.saveInProgress) {
      this.saveInProgress = true;
      setTimeout(() => {
        this.triggerAction(action);
      });
    }
  }

  /** Private methods */
  /**
   * Set the roles for the current user
   */
  private setRolesForUser() {
    this.isAdminUser = this.appStorage.userIsInRole(['Admin']);

    //new roles #16266
    this.isProjectTeam = this.appStorage.userIsInRole(['Project Team']);
    this.isValuer = this.appStorage.userIsInRole(['Valuer']);
    this.isClientAccount = this.appStorage.userIsInRole(['Client Account']);
    this.isGeoLead = this.appStorage.userIsInRole(['Geo Lead']);
    this.isSectorLead = this.appStorage.userIsInRole(['Sector Lead']);
    this.isBusinessAnalyst = this.appStorage.userIsInRole(['Business Analyst']);
    this.isDataverseDeveloper = this.appStorage.userIsInRole(['Dataverse Developer']);
    this.isDataverseConsultant = this.appStorage.userIsInRole(['Dataverse Consultant']);
    this.isDataverseKeyUser = this.appStorage.userIsInRole(['Dataverse Key User']);

    this.isAPT = this.isAdminUser || this.isProjectTeam;
    this.isAPTV = this.isAdminUser || this.isProjectTeam || this.isValuer;

    this.ref.markForCheck();

    setTimeout(() => {
      this.eventBus.broadcastEvent('NavigationActionBase:UserRolesAcquired', this);
    });
  }
  /**
   * Handle events
   */
  private handleEvents() {
    this.eventBus.broadcastEvent('searchBarVisibility', false, '');

    this.onEvents.push(this.eventBus.subscribeEvent('showSaveCancelNavigationAction', (visible) => {
      this.showHideSaveCancelActions(visible);
    }));
    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', (_tab, form) => {
      if (!form) {
        this.showHideSaveCancelActions(false);
      }
    }));
    this.onEvents.push(this.eventBus.subscribeEvent('comparablesTabSelect', () => {
      this.showHideSaveCancelActions(false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('afterResetShortNavigation', () => {
      this.toggleOrganization = false;
      this.toggleData = false;
      this.groupingItemSelected = null;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('projectMainTabSelect', () => {
      this.showHideSaveCancelActions(false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('enableSaveButtonIfRequired', () => {
      // little delay to enable button, so things can cleanup (closing form/warning dialogs etc..)
      setTimeout(() => {
        this.saveInProgress = false;
        this.ref.markForCheck();
      }, 200);
    }));
  }
  /**
   * Handle for open state events
   */
  private handleFormOpenStateEvents() {
    this.onEvents.push(this.eventBus.subscribeEvent('showGridChanged', (param) => {
      const formOpenedState = !param.value;
      if (this.formOpened != formOpenedState) {
        this.formOpened = formOpenedState;
        this.ref.markForCheck();
      }
    }));
  }
  /**
   * Show, hide, save and cancel actions
   * @param visible
   */
  private showHideSaveCancelActions(visible: boolean) {
    this.saveInProgress = false;//if we handle this, save is done (if save was triggered), so mark save as finished (so save button is enabled again)
    this.displaySaveCancelActions = visible;
    this.toggleData = false;
    if (visible) {
      this.onGroupingItemSelected('Data');
    } else {
      this.eventBus.broadcastEvent('resetShortNavigation');
    }
  }
  /**
   * Sets active tab
   * @param tab
   */
  private setActiveTab(tab: string) {
    let activeTab: ProjectValuationTabs;

    if (tab === 'tabValuationPortfolio') {
      activeTab = ProjectValuationTabs.ValPortofolios;
      this.navigationService.setActiveProjectValuationTab(activeTab);
    }
    if (tab === 'tabReport') {
      activeTab = ProjectValuationTabs.Dashboards;
      this.navigationService.setActiveProjectValuationTab(activeTab);
    }
  }
  /**
   * Display full navigation
   */
  displayFullNavigation() {
    this.eventBus.broadcastEvent('displayFullNavigation');
  }
}
